import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  UserCog, 
  Plus, 
  Edit2, 
  Trash2, 
  Shield,
  ShieldCheck,
  ShieldAlert,
  User,
  CheckCircle,
  XCircle,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { value: 'SUPERADMIN', label: 'Super Admin', icon: ShieldAlert, color: 'text-red-500' },
  { value: 'ADMIN', label: 'Administrador', icon: ShieldCheck, color: 'text-brand-main' },
  { value: 'RECEPTIONIST', label: 'Recepcionista', icon: Shield, color: 'text-brand-cyan' },
];

const initialFormData = {
  username: '',
  password: '',
  full_name: '',
  role: 'RECEPTIONIST',
};

const Users = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [userToDeactivate, setUserToDeactivate] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData(initialFormData);
    setIsModalOpen(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      password: '',
      full_name: user.full_name,
      role: user.role,
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username || !formData.full_name || !formData.role) {
      toast.error('Campos requeridos faltantes', {
        description: 'Por favor complete todos los campos requeridos',
      });
      return;
    }

    if (!editingUser && !formData.password) {
      toast.error('Contraseña requerida', {
        description: 'La contraseña es requerida para nuevos usuarios',
      });
      return;
    }

    setSaving(true);
    try {
      if (editingUser) {
        const updateData = {
          full_name: formData.full_name,
          role: formData.role,
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await axios.put(`${API_URL}/api/users/${editingUser.id}`, updateData);
        toast.success('Usuario actualizado exitosamente');
      } else {
        await axios.post(`${API_URL}/api/users`, formData);
        toast.success('Usuario creado exitosamente', {
          description: `${formData.full_name} ahora puede acceder al sistema`,
        });
      }
      setIsModalOpen(false);
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      const message = error.response?.data?.detail || 'Error al guardar usuario';
      toast.error('Error al guardar', { description: message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este usuario?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/users/${id}`);
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      const message = error.response?.data?.detail || 'Error al eliminar usuario';
      toast.error(message);
    }
  };

  const openDeactivateDialog = (user) => {
    setUserToDeactivate(user);
    setDeactivateDialogOpen(true);
  };

  const confirmDeactivate = async () => {
    if (!userToDeactivate) return;
    
    try {
      await axios.put(`${API_URL}/api/users/${userToDeactivate.id}`, {
        is_active: !userToDeactivate.is_active,
      });
      fetchUsers();
      toast.success(userToDeactivate.is_active 
        ? `${userToDeactivate.full_name} ha sido desactivado` 
        : `${userToDeactivate.full_name} ha sido activado`
      );
      setDeactivateDialogOpen(false);
      setUserToDeactivate(null);
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Error al actualizar estado');
    }
  };

  const toggleActive = async (user) => {
    if (user.is_active) {
      openDeactivateDialog(user);
    } else {
      // Activating doesn't need confirmation
      try {
        await axios.put(`${API_URL}/api/users/${user.id}`, {
          is_active: true,
        });
        fetchUsers();
        toast.success(`${user.full_name} ha sido activado`);
      } catch (error) {
        console.error('Error updating user:', error);
        toast.error('Error al actualizar estado');
      }
    }
  };

  const getRoleInfo = (role) => {
    return ROLES.find(r => r.value === role) || ROLES[2];
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="font-heading text-2xl font-bold text-slate-900">Gestión de Usuarios</h2>
        <Button 
          onClick={openCreateModal}
          className="bg-[#1a5192] hover:bg-[#0d3a6e] text-white font-semibold shadow-lg"
          data-testid="new-user-button"
        >
          <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
          Nuevo Usuario
        </Button>
      </div>

      {/* Users Table */}
      <Card className="bg-white border-slate-100 shadow-soft overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table data-testid="users-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold text-slate-700">Usuario</TableHead>
                  <TableHead className="font-semibold text-slate-700">Nombre Completo</TableHead>
                  <TableHead className="font-semibold text-slate-700">Rol</TableHead>
                  <TableHead className="font-semibold text-slate-700">Estado</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12">
                      <div className="flex items-center justify-center gap-2 text-slate-400">
                        <div className="w-5 h-5 border-2 border-slate-300 border-t-brand-main rounded-full animate-spin"></div>
                        Cargando...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12 text-slate-400">
                      No hay usuarios registrados
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => {
                    const roleInfo = getRoleInfo(user.role);
                    const RoleIcon = roleInfo.icon;
                    const isCurrentUser = currentUser?.id === user.id;
                    
                    return (
                      <TableRow 
                        key={user.id} 
                        className="hover:bg-slate-50 transition-colors"
                        data-testid={`user-row-${user.id}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-brand-main/10 flex items-center justify-center">
                              <User className="w-5 h-5 text-brand-main" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{user.username}</p>
                              {isCurrentUser && (
                                <span className="text-xs text-brand-cyan">(Tú)</span>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-700">{user.full_name}</TableCell>
                        <TableCell>
                          <Badge 
                            variant="secondary"
                            className={`bg-slate-100 border-0 ${roleInfo.color}`}
                          >
                            <RoleIcon className="w-3 h-3 mr-1" />
                            {roleInfo.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={user.is_active}
                              onCheckedChange={() => toggleActive(user)}
                              disabled={isCurrentUser}
                              data-testid={`toggle-active-${user.id}`}
                            />
                            <span className={`text-sm ${user.is_active ? 'text-emerald-600' : 'text-slate-400'}`}>
                              {user.is_active ? 'Activo' : 'Inactivo'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditModal(user)}
                              className="h-8 w-8 text-slate-500 hover:text-brand-main hover:bg-brand-main/10"
                              data-testid={`edit-user-${user.id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            {!isCurrentUser && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(user.id)}
                                className="h-8 w-8 text-slate-500 hover:text-red-500 hover:bg-red-50"
                                data-testid={`delete-user-${user.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* User Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="user-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-semibold text-slate-900 flex items-center gap-2">
              <UserCog className="w-5 h-5 text-brand-main" />
              {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Usuario *</Label>
              <Input
                id="username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder="nombre_usuario"
                className="bg-slate-50"
                disabled={!!editingUser}
                data-testid="user-username"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="full_name">Nombre Completo *</Label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                placeholder="Nombre completo"
                className="bg-slate-50"
                data-testid="user-fullname"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">
                Contraseña {editingUser ? '(dejar vacío para mantener)' : '*'}
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={editingUser ? '••••••••' : 'Contraseña'}
                className="bg-slate-50"
                data-testid="user-password"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Rol *</Label>
              <Select 
                value={formData.role} 
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger className="bg-slate-50" data-testid="user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((role) => {
                    const RoleIcon = role.icon;
                    return (
                      <SelectItem key={role.value} value={role.value}>
                        <div className="flex items-center gap-2">
                          <RoleIcon className={`w-4 h-4 ${role.color}`} />
                          {role.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            <DialogFooter className="gap-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsModalOpen(false)}
                className="border-slate-300 text-slate-700"
              >
                Cancelar
              </Button>
              <Button 
                type="submit"
                className="bg-[#1a5192] hover:bg-[#0d3a6e] text-white font-semibold"
                data-testid="save-user-button"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  editingUser ? 'Actualizar' : 'Crear Usuario'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Deactivate User Confirmation Dialog */}
      <AlertDialog open={deactivateDialogOpen} onOpenChange={setDeactivateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Desactivar este usuario?</AlertDialogTitle>
            <AlertDialogDescription>
              {userToDeactivate && (
                <>
                  Está a punto de desactivar a <strong>{userToDeactivate.full_name}</strong>.
                  <br /><br />
                  El usuario no podrá acceder al sistema hasta que sea reactivado.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeactivate}
              className="bg-amber-500 hover:bg-amber-600"
            >
              Sí, desactivar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Users;
